import Joi from "joi";
import { useEffect, useState } from "react";
import { FormattedMessage, useIntl } from "react-intl";
import { useNavigate } from "react-router-dom";
import { Col, Row } from "reactstrap";
import { ClientService } from "../../services/ClientService";
import { CreateProjectDTO } from "../../models/Project/CreateProjectDTO";
import { SearchByClientName } from "../../models/Client/SearchByClientName";
import { ProjectService } from "../../services/ProjectService";
import { SelectItemsProps } from "../../components/Select2Component";
import Toast from "../../helpers/Toast";
import InputComponent from "../../components/InputComponent";
import SearchSelectComponent from "../../components/SearchSelectComponent";
import ButtonComponent from "../../components/ButtonComponent";
import { Utilities } from "../../helpers/Utilities";
import Layout from "../../components/Layout/Layout";
import PageTitle from "../../components/Layout/PageTitle";

export default function ProjectCreate() {
    const intl = useIntl();
    const navigate = useNavigate();
    const clientService = new ClientService();
    const projectService = new ProjectService();
    const [isButtonCreateLoading, setIsButtonCreateLoading] = useState<boolean>(false);
    const erpId = "";

    //Loadings
    const [loading, setLoading] = useState<boolean>(true);
    const [loadingCreate, setLoadingCreate] = useState<boolean>(false);

    const [inputDescription, setInputDescription] = useState<string>("");
    const [clientSelected, setclientSelected] = useState<SelectItemsProps | null>(null);
    const [inputStartDate, setInputStartDate] = useState<Date>();
    const [inputBudget, setInputBudget] = useState<number>(0);
    const [inputNotes, setInputNotes] = useState<string>("");

    const schemaEquipmentValidation = Joi.object({
        description: Joi.string()
            .required()
            .messages({
                "any.required": intl.formatMessage({
                    id: "Screens.Projects.Validation.DescriptionRequired",
                }),
                "string.base": intl.formatMessage({
                    id: "Screens.Projects.Validation.DescriptionRequired",
                }),
                "string.empty": intl.formatMessage({
                    id: "Screens.Projects.Validation.DescriptionRequired",
                }),
            }),
        client: Joi.string()
            .required()
            .messages({
                "any.required": intl.formatMessage({
                    id: "Screens.Projects.Validation.ClientRequired",
                }),
                "string.base": intl.formatMessage({
                    id: "Screens.Projects.Validation.ClientRequired",
                }),
                "string.empty": intl.formatMessage({
                    id: "Screens.Projects.Validation.ClientRequired",
                }),
            }),
    });

    useEffect(() => {
        setLoading(false);
    }, []);

    const createProject = async () => {
        validateProject(inputDescription, clientSelected?.label).then(async (result: any) => {
            if (result.error != null) {
                var message = result.error!.message;
                Toast.Show("error", message);
                return;
            }

            const createProject: CreateProjectDTO = {
                erpId: erpId,
                description: inputDescription,
                startDate: inputStartDate || new Date(),
                budgetSellingPrice: inputBudget || 0,
                budgetCostPrice: inputBudget || 0,
                notes: inputNotes || "",
                clientId: clientSelected?.value ?? "0",
                // Usei o operador de coalescência nula
                // parecido ao "??" mas "||" aceita valores (0, "", undefined, false,.. e não apenas "" e undefined )
            };


            setIsButtonCreateLoading(true);
            const response = await projectService.Create(createProject);
            setIsButtonCreateLoading(false);

            if (response.success !== true) {
                Toast.Show("error", response.message);
                setLoadingCreate(false);
                return;
            }

            Toast.Show("success", response.message);
            setLoadingCreate(false);
            navigate(-1);
        });
    };

    const validateProject = (description: string, client: any) => {
        return new Promise(function (resolve) {
            var validationResult = schemaEquipmentValidation.validate(
                {
                    description: description,
                    client: client,
                },
                { abortEarly: true }
            );
            resolve(validationResult);
        });
    };

    const GetByClient = async (value: string): Promise<SelectItemsProps[]> => {
        var data: SearchByClientName = {
            name: value,
        };
        var response = await clientService.SearchByName(data);
        if (response.success === false || response.obj === null) {
            Toast.Show("error", response.message);
            return [];
        }

        const list: SelectItemsProps[] = [];

        response.obj.forEach((element: any) => {
            var item: SelectItemsProps = {
                value: element.erpId,
                label: element.name,
            };
            list.push(item);
        });

        return list;
    };

    return (
        <>
            <Layout>
                <div className="pageContainer">
                    <div className="pageHeader">
                        <Row style={{ margin: "0px", padding: "0px" }}>
                            <Col style={{ margin: "0px", padding: "0px" }}>
                                <PageTitle
                                    title={`${intl.formatMessage({
                                        id: "Screens.ProjectCreate.Title",
                                    })}:`}
                                    allowGoBack={true}
                                />
                            </Col>
                        </Row>
                    </div>

                    {loading === false && (
                        <div className="pageBody">
                            <label style={{ margin: "0px", padding: "13px" }}>
                                <b>
                                    <FormattedMessage id="Screens.ProjectCreate.SubTitle" />
                                </b>
                            </label>
                            <Col xl={6}>
                                <Row
                                    style={{
                                        margin: "0px",
                                        padding: "0px",
                                        alignItems: "flex-end",
                                        alignContent: "flex-end",
                                    }}
                                >
                                    <Row>
                                        <InputComponent
                                            xl={8}
                                            lg={8}
                                            md={8}
                                            sm={8}
                                            xs={8}
                                            style={{ width: "100%" }}
                                            type={"text"}
                                            value={inputDescription}
                                            label={intl.formatMessage({
                                                id: "Screens.Projects.Info.Description",
                                            })}
                                            readOnly={false}
                                            onChange={(t: string) => setInputDescription(t)}
                                        />
                                    </Row>
                                    <Row>
                                        <InputComponent
                                            xl={4}
                                            lg={4}
                                            md={4}
                                            sm={4}
                                            xs={4}
                                            style={{ width: "100%" }}
                                            type={"number"}
                                            value={inputBudget}
                                            label={intl.formatMessage({
                                                id: "Screens.Projects.Info.BudgetSellingPrice",
                                            })}
                                            readOnly={false}
                                            onChange={(t: number) => setInputBudget(t)}
                                        />
                                        <InputComponent
                                            xl={4}
                                            lg={4}
                                            md={4}
                                            sm={4}
                                            xs={4}
                                            style={{ width: "100%" }}
                                            type={"date"}
                                            value={inputStartDate ? Utilities.formatDateYYYYMMDD(new Date(inputStartDate)) : ""}
                                            label={intl.formatMessage({
                                                id: "Screens.Projects.Info.StartDate",
                                            })}
                                            readOnly={false}
                                            onChange={(date: Date) => setInputStartDate(date)}
                                        />
                                    </Row>
                                    <Row>
                                        <InputComponent
                                            xl={8}
                                            lg={8}
                                            md={8}
                                            sm={8}
                                            xs={8}
                                            style={{ width: "100%" }}
                                            type={"text"}
                                            value={inputNotes}
                                            label={intl.formatMessage({
                                                id: "Screens.Projects.Info.Notes",
                                            })}
                                            readOnly={false}
                                            onChange={(t: string) => setInputNotes(t)}
                                        />
                                    </Row>
                                </Row>

                                <label style={{ margin: "0px", padding: "13px" }}>
                                    <b>
                                        <FormattedMessage id="Screens.ProjectCreate.SubTitle2" />
                                    </b>
                                </label>
                                <Row
                                    style={{
                                        margin: "0px",
                                        padding: "0px",
                                        alignItems: "flex-end",
                                        alignContent: "flex-end",
                                    }}
                                >
                                    <SearchSelectComponent
                                        xl={8}
                                        lg={8}
                                        md={8}
                                        sm={8}
                                        xs={8}
                                        label={intl.formatMessage({
                                            id: "Screens.Projects.Info.Client",
                                        })}
                                        loadOptions={GetByClient}
                                        onChange={(e) => {
                                            setclientSelected(e);
                                        }}
                                        value={clientSelected}
                                        style={{ width: "100%" }}
                                    />
                                </Row>
                            </Col>

                            <Row style={{ margin: "0px", padding: "0px" }}>
                                <Col xl={1} lg={1} md={1} sm={1} xs={1}>
                                    <ButtonComponent
                                        loading={isButtonCreateLoading}
                                        style={{ width: "100%", marginTop: "10px" }}
                                        text={intl.formatMessage({ id: "ProjectCreate.Save" })}
                                        onClick={() => createProject()}
                                    />
                                </Col>
                            </Row>
                        </div>
                    )}
                </div>
            </Layout >
        </>
    );
}
